import { ITEMS, PLAYER } from './constants';
import { ATTACKS, type AttackStyle } from './combat';
import { WEAPONS, type WeaponType } from './weapons';

export const COMBAT_TUNING_STORAGE_KEY = 'futbol-fighters-combat-tuning-v1';
export const COMBAT_CHARACTERS = [
  { id: 'a-gk', name: 'PULPO · Portero azul' },
  { id: 'a-1', name: 'MATEO · Azul' },
  { id: 'a-2', name: 'NICO · Azul' },
  { id: 'b-gk', name: 'TIBURÓN · Portero rojo' },
  { id: 'b-1', name: 'TIAGO · Rojo' },
  { id: 'b-2', name: 'DANTE · Rojo' },
] as const;

export interface CombatTuning {
  attackDamage: Record<AttackStyle, number>;
  weaponDamage: Record<WeaponType, number>;
  specialDamage: { slide: number; hammer: number; bomb: number };
  characterHp: Record<(typeof COMBAT_CHARACTERS)[number]['id'], number>;
  sfxVolume: number;
  musicVolume: number;
}

export const DEFAULT_COMBAT_TUNING: CombatTuning = {
  attackDamage: Object.fromEntries(Object.entries(ATTACKS).map(([key, attack]) => [key, attack.damage])) as CombatTuning['attackDamage'],
  weaponDamage: Object.fromEntries(Object.entries(WEAPONS).map(([key, weapon]) => [key, weapon.damage])) as CombatTuning['weaponDamage'],
  specialDamage: { slide: PLAYER.slideDamage, hammer: ITEMS.hammerDamage, bomb: ITEMS.bombDamage },
  characterHp: Object.fromEntries(COMBAT_CHARACTERS.map(({ id }) => [id, PLAYER.maxHp])) as CombatTuning['characterHp'],
  sfxVolume: 100,
  musicVolume: 100,
};

function bounded(value: unknown, fallback: number, min: number, max: number): number {
  const number = Number(value);
  return Number.isFinite(number) ? Math.max(min, Math.min(max, Math.round(number))) : fallback;
}

export function normalizeCombatTuning(value: unknown): CombatTuning {
  const source = value && typeof value === 'object' ? value as Record<string, any> : {};
  const attacks = source.attackDamage && typeof source.attackDamage === 'object' ? source.attackDamage : {};
  const weapons = source.weaponDamage && typeof source.weaponDamage === 'object' ? source.weaponDamage : {};
  const specials = source.specialDamage && typeof source.specialDamage === 'object' ? source.specialDamage : {};
  const hp = source.characterHp && typeof source.characterHp === 'object' ? source.characterHp : {};
  return {
    attackDamage: Object.fromEntries(Object.entries(DEFAULT_COMBAT_TUNING.attackDamage).map(([key, fallback]) => [key, bounded(attacks[key], fallback, 0, 1000)])) as CombatTuning['attackDamage'],
    weaponDamage: Object.fromEntries(Object.entries(DEFAULT_COMBAT_TUNING.weaponDamage).map(([key, fallback]) => [key, bounded(weapons[key], fallback, 0, 1000)])) as CombatTuning['weaponDamage'],
    specialDamage: {
      slide: bounded(specials.slide, DEFAULT_COMBAT_TUNING.specialDamage.slide, 0, 1000),
      hammer: bounded(specials.hammer, DEFAULT_COMBAT_TUNING.specialDamage.hammer, 0, 1000),
      bomb: bounded(specials.bomb, DEFAULT_COMBAT_TUNING.specialDamage.bomb, 0, 1000),
    },
    characterHp: Object.fromEntries(COMBAT_CHARACTERS.map(({ id }) => [id, bounded(hp[id], PLAYER.maxHp, 1, 1000)])) as CombatTuning['characterHp'],
    sfxVolume: bounded(source.sfxVolume, 100, 0, 100),
    musicVolume: bounded(source.musicVolume, 100, 0, 100),
  };
}

function load(): CombatTuning {
  try { return normalizeCombatTuning(JSON.parse(localStorage.getItem(COMBAT_TUNING_STORAGE_KEY) ?? 'null')); }
  catch { return normalizeCombatTuning(null); }
}

let current = load();

export function getCombatTuning(): CombatTuning {
  return { ...current, attackDamage: { ...current.attackDamage }, weaponDamage: { ...current.weaponDamage }, specialDamage: { ...current.specialDamage }, characterHp: { ...current.characterHp } };
}

export function saveCombatTuning(value: unknown): { settings: CombatTuning; persisted: boolean } {
  current = normalizeCombatTuning(value);
  try {
    localStorage.setItem(COMBAT_TUNING_STORAGE_KEY, JSON.stringify(current));
    return { settings: getCombatTuning(), persisted: true };
  } catch {
    return { settings: getCombatTuning(), persisted: false };
  }
}

export function getAttackDamage(style: AttackStyle): number { return current.attackDamage[style]; }
export function getWeaponDamage(type: WeaponType): number { return current.weaponDamage[type]; }
export function getSpecialDamage(type: keyof CombatTuning['specialDamage']): number { return current.specialDamage[type]; }
